import { useEffect, useRef } from "react";

/**
 * useSharedScroll — a single global scroll broadcaster.
 *
 * WHY THIS EXISTS
 * ---------------
 * Before: every scroll-driven section on the homepage attached its OWN
 * `window.scroll` listener, its OWN rAF throttle, and its OWN
 * `getBoundingClientRect()` call to compute where its own section sits
 * within the viewport. With 6 such sections on the home page, a single
 * scroll pixel triggered:
 *   · 6 listener callbacks
 *   · 6 rAF schedules
 *   · 6 `getBoundingClientRect()` calls — EACH of which is a forced
 *     synchronous layout, the single most expensive scroll-time operation
 *     in a browser.
 *
 * With this shared broadcaster:
 *   · Exactly 1 scroll listener for the entire page.
 *   · Exactly 1 rAF per animation frame (single-flight).
 *   · Zero `getBoundingClientRect()` on the scroll path — each section
 *     caches its `offsetTop` + `offsetHeight` at mount/resize and derives
 *     its scroll progress from a plain number (`window.scrollY`).
 *
 * HOW SECTIONS USE IT
 * -------------------
 *   const sectionRef = useRef<HTMLElement>(null);
 *   useSectionScrollProgress(sectionRef, (progress, inView) => {
 *     // progress is 0..1 across the section's scroll runway
 *     // called at most once per animation frame
 *   });
 *
 * The callback receives the current 0..1 scroll progress across that
 * section's runway (`(scrollY - offsetTop) / (offsetHeight - viewportH)`),
 * plus an `inView` boolean so sections can short-circuit when off-screen.
 */

type Listener = (scrollY: number, viewportH: number) => void;

const listeners = new Set<Listener>();
let rafPending = false;
let initialized = false;

const flush = () => {
  rafPending = false;
  const y = window.scrollY || window.pageYOffset || 0;
  const vh = window.innerHeight;
  // Iterate over a snapshot so listeners can safely add/remove during dispatch
  const snapshot = Array.from(listeners);
  for (let i = 0; i < snapshot.length; i++) {
    try {
      snapshot[i](y, vh);
    } catch {
      /* one listener throwing must not break the rest */
    }
  }
};

const schedule = () => {
  if (rafPending) return;
  rafPending = true;
  requestAnimationFrame(flush);
};

const ensureInit = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
};

/**
 * Low-level primitive: subscribe a listener to the shared scroll pulse.
 * The listener is invoked once immediately after subscription, then at
 * most once per animation frame while the user is scrolling / resizing.
 */
export function useSharedScroll(cb: Listener) {
  useEffect(() => {
    ensureInit();
    listeners.add(cb);
    // Prime once so newly-mounted subscribers get an initial value.
    schedule();
    return () => {
      listeners.delete(cb);
    };
  }, [cb]);
}

/**
 * High-level helper: given a ref to a section element, invoke `cb` with
 * the 0..1 progress across that section's scroll runway (sticky-style
 * sections use `height: Nvh` where scroll runway = height - viewportH).
 *
 * Uses `offsetTop` / `offsetHeight` (cached at mount + on resize) to
 * derive progress from `window.scrollY` — NO `getBoundingClientRect()`
 * on the scroll path, so zero forced synchronous layout per frame.
 *
 * The `inView` flag is `false` when the section is entirely above or
 * below the viewport (plus a 50px margin), so callbacks can early-exit.
 */
export function useSectionScrollProgress(
  ref: React.RefObject<HTMLElement>,
  cb: (progress: number, inView: boolean) => void,
) {
  const cbRef = useRef(cb);
  cbRef.current = cb;

  // Cached geometry — refreshed on mount, resize, and via ResizeObserver.
  const offsetTopRef = useRef(0);
  const offsetHeightRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      // offsetTop is relative to the offsetParent — walk up to get absolute Y.
      let top = 0;
      let node: HTMLElement | null = el;
      while (node) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      offsetTopRef.current = top;
      offsetHeightRef.current = el.offsetHeight;
    };

    measure();

    const onListener: Listener = (scrollY, viewportH) => {
      const top = offsetTopRef.current;
      const height = offsetHeightRef.current;
      const scrollable = height - viewportH;
      // In-view test (with 50px slack), derived from cached values only.
      const sectionBottomInPage = top + height;
      const viewTop = scrollY;
      const viewBottom = scrollY + viewportH;
      const inView =
        sectionBottomInPage > viewTop - 50 && top < viewBottom + 50;
      let progress = 0;
      if (scrollable > 0) {
        progress = Math.max(0, Math.min(1, (scrollY - top) / scrollable));
      } else if (scrollY >= top) {
        progress = 1;
      }
      cbRef.current(progress, inView);
    };

    listeners.add(onListener);
    ensureInit();
    schedule();

    // Re-measure whenever the element itself changes size, or when
    // ancestors reflow (window resize handled via the shared listener).
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            measure();
            schedule();
          })
        : null;
    if (ro) ro.observe(el);

    const onResize = () => {
      measure();
    };
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      listeners.delete(onListener);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [ref]);
}